import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import {
  MarketingLayout,
  SectionHeader,
  useHydratedTranslation,
} from "@/components/marketing/Layout";
import { COMPETITOR_BY_SLUG } from "@/lib/comparisons";
import { useAutoTranslate } from "@/components/AutoTranslate";
import { ArrowRight, Check, Loader2 } from "lucide-react";

const SITE = "https://realitytwin.io";

export const Route = createFileRoute("/vs/$slug")({
  head: ({ params }) => {
    const c = COMPETITOR_BY_SLUG[params.slug];
    if (!c)
      return buildSeo({
        path: `/vs/${params.slug}`,
        title: "Comparison — Reality Twin",
        description: "Honest, side-by-side comparisons of Reality Twin vs. the alternatives.",
      });
    const url = `${SITE}/vs/${c.slug}`;
    const seo = buildSeo({
      path: `/vs/${c.slug}`,
      title: c.metaTitle,
      description: c.metaDescription,
      type: "article",
    });
    return {
      ...seo,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: c.metaTitle,
            description: c.metaDescription,
            author: { "@type": "Person", name: "Ajdin Ruznic" },
            publisher: { "@type": "Organization", name: "Reality Twin", url: SITE },
            mainEntityOfPage: url,
            about: [
              { "@type": "Thing", name: `${c.name} alternative` },
              { "@type": "Thing", name: "AI agent for B2B" },
              { "@type": "Thing", name: "Twin-to-Twin discovery" },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: c.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  loader: ({ params }) => {
    if (!COMPETITOR_BY_SLUG[params.slug]) throw notFound();
    return null;
  },
  component: VsPage,
});

function VsPage() {
  const t = useHydratedTranslation();
  const { slug } = Route.useParams();
  const c = COMPETITOR_BY_SLUG[slug];

  // The chrome (headings, buttons) is localized via i18n; the competitor-
  // specific copy lives in comparisons.ts in English. Translate it into the
  // reader's language through the cached translator, preserving order.
  const src = c
    ? [
        c.category,
        c.heroEmphasis,
        c.intro,
        c.shortAnswer,
        ...c.rows.flatMap((r) => [r[0], r[1], r[2]]),
        ...c.differentiators.flatMap((d) => [d.title, d.body]),
        ...c.whenThem,
        ...c.whenUs,
        c.migration,
        ...c.faqs.flatMap((f) => [f.q, f.a]),
      ]
    : [];
  const { out, busy } = useAutoTranslate(src);
  if (!c) return null;
  let i = 0;
  const category = out[i++];
  const heroEmphasis = out[i++];
  const intro = out[i++];
  const shortAnswer = out[i++];
  const rows = c.rows.map(() => [out[i++], out[i++], out[i++]] as [string, string, string]);
  const differentiators = c.differentiators.map(() => ({ title: out[i++], body: out[i++] }));
  const whenThem = c.whenThem.map(() => out[i++]);
  const whenUs = c.whenUs.map(() => out[i++]);
  const migration = out[i++];
  const faqs = c.faqs.map(() => ({ q: out[i++], a: out[i++] }));

  return (
    <MarketingLayout>
      <article className="py-16 sm:py-24">
        {/* HERO */}
        <div className="mx-auto max-w-3xl px-6">
          <div className="font-mono-eyebrow flex items-center gap-2">
            {t("routes.vs.eyebrow")} · {category}
            {busy && <Loader2 className="h-3 w-3 animate-spin" />}
          </div>
          <h1 className="mt-4 font-serif text-[40px] sm:text-[60px] leading-[1.02] tracking-[-0.03em]">
            Reality Twin vs. {c.name}:{" "}
            <em className="italic text-muted-foreground">{heroEmphasis}</em>
          </h1>
          <p className="mt-6 text-[18px] leading-[1.6] text-muted-foreground">{intro}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            <Link
              to="/auth"
              className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-[13.5px] font-medium text-background hover:opacity-90"
            >
              {t("routes.vs.startFree")} <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
            <Link
              to="/compare"
              className="inline-flex items-center rounded-full hairline px-4 py-2 text-[13.5px]"
            >
              {t("routes.vs.allComparisons")}
            </Link>
          </div>
        </div>

        <div className="mx-auto max-w-3xl px-6 mt-14 space-y-10 text-[16.5px] leading-[1.75] text-foreground/90">
          {/* SHORT ANSWER */}
          <section>
            <h2 className="font-serif text-[30px] leading-[1.1] tracking-[-0.02em]">
              {t("routes.vs.shortAnswer")}
            </h2>
            <p className="mt-4 text-muted-foreground">{shortAnswer}</p>
          </section>

          {/* MATRIX */}
          <section>
            <h2 className="font-serif text-[30px] leading-[1.1] tracking-[-0.02em]">
              {t("routes.vs.sideBySide")}
            </h2>
            {/* Three columns of prose would be ~110px each on a phone. Below
                sm the row becomes a stacked block with its own labels; from sm
                up it's the familiar side-by-side table. */}
            <div className="mt-6 hairline rounded-xl overflow-hidden">
              <div className="hidden sm:grid grid-cols-3 bg-cream text-[13px] font-medium">
                <div className="p-4">{t("routes.vs.capability")}</div>
                <div className="p-4 border-l border-border">{c.name}</div>
                <div className="p-4 border-l border-border">Reality Twin</div>
              </div>
              {rows.map(([cap, them, us]) => (
                <div
                  key={cap}
                  className="grid grid-cols-1 sm:grid-cols-3 border-t border-border text-[14px] first:border-t-0 sm:first:border-t"
                >
                  <div className="px-4 pt-4 pb-2 sm:p-4 font-medium">{cap}</div>
                  <div className="px-4 pb-2 sm:p-4 sm:border-l border-border text-muted-foreground">
                    <span className="sm:hidden block text-[11px] uppercase tracking-wide text-muted-foreground/70 mb-0.5">
                      {c.name}
                    </span>
                    {them}
                  </div>
                  <div className="px-4 pb-4 sm:p-4 sm:border-l border-border">
                    <span className="sm:hidden block text-[11px] uppercase tracking-wide text-muted-foreground/70 mb-0.5">
                      Reality Twin
                    </span>
                    {us}
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[12px] text-muted-foreground">
              {t("routes.vs.disclaimer", { name: c.name })}
            </p>
          </section>

          {/* DIFFERENTIATORS */}
          {differentiators.map((d, idx) => (
            <section key={idx}>
              <h2 className="font-serif text-[30px] leading-[1.1] tracking-[-0.02em]">
                {idx + 1} · {d.title}
              </h2>
              <p className="mt-4 text-muted-foreground">{d.body}</p>
            </section>
          ))}

          {/* WHEN TO USE */}
          <section className="grid md:grid-cols-2 gap-6">
            <div className="hairline rounded-xl bg-card p-7">
              <div className="font-mono-eyebrow">{t("routes.vs.whenThem", { name: c.name })}</div>
              <ul className="mt-4 space-y-2.5">
                {whenThem.map((line) => (
                  <li
                    key={line}
                    className="flex gap-2 text-[14px] leading-relaxed text-muted-foreground"
                  >
                    <Check className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="hairline rounded-xl bg-card p-7">
              <div className="font-mono-eyebrow">{t("routes.vs.whenUs")}</div>
              <ul className="mt-4 space-y-2.5">
                {whenUs.map((line) => (
                  <li key={line} className="flex gap-2 text-[14px] leading-relaxed text-foreground">
                    <Check className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* MIGRATION */}
          <section>
            <h2 className="font-serif text-[30px] leading-[1.1] tracking-[-0.02em]">
              {t("routes.vs.migrating")}
            </h2>
            <p className="mt-4 text-muted-foreground">{migration}</p>
          </section>

          {/* CTA */}
          <section className="hairline rounded-xl bg-cream p-8 sm:p-10">
            <div className="font-mono-eyebrow">Reality Twin</div>
            <h3 className="mt-3 font-serif text-[28px] leading-[1.1] tracking-[-0.02em]">
              {t("routes.vs.ctaTitle")}
            </h3>
            <p className="mt-3 text-[15px] text-muted-foreground">{t("routes.vs.ctaBody")}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/auth"
                className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-[13.5px] font-medium text-background hover:opacity-90"
              >
                {t("routes.vs.startFree")} <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center rounded-full hairline px-4 py-2 text-[13.5px]"
              >
                {t("routes.vs.seePricing")}
              </Link>
              <Link
                to="/platform"
                className="inline-flex items-center rounded-full hairline px-4 py-2 text-[13.5px]"
              >
                {t("routes.vs.howItWorks")}
              </Link>
            </div>
          </section>

          {/* FAQ */}
          <section>
            <SectionHeader
              eyebrow={t("routes.vs.faqEyebrow")}
              title={<>{t("routes.vs.faqTitle")}</>}
            />
            <div className="mt-8 space-y-6">
              {faqs.map((f) => (
                <div key={f.q} className="hairline rounded-xl bg-card p-6">
                  <h3 className="font-serif text-[20px] leading-tight">{f.q}</h3>
                  <p className="mt-2 text-[14.5px] leading-relaxed text-muted-foreground">{f.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* RELATED */}
          <section>
            <div className="font-mono-eyebrow">{t("routes.vs.keepReading")}</div>
            <div className="mt-4">
              <Link
                to="/compare"
                className="hairline rounded-xl bg-card p-5 hover:bg-cream transition block"
              >
                <div className="font-serif text-[18px] leading-tight">
                  {t("routes.vs.allComparisons")}
                </div>
                <div className="mt-1 text-[13px] text-muted-foreground">
                  {t("routes.vs.compareSub")}
                </div>
              </Link>
            </div>
          </section>
        </div>
      </article>
    </MarketingLayout>
  );
}
